import { useRef, useState } from 'react'
import { ModalForm, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { Alert, Button, Form, Input, Select } from 'antd'
import { useAccess } from '@/hooks/use-access.ts'
import { usePageTableNavigation } from '@/hooks/use-page-table.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { botRunsPersistence, botRunsSearchSchema, type BotRunsSearch } from './bot-runs-search.ts'

const route = getRouteApi('/_authenticated/system-management/bots/$botId')
export function BotRuns({ botId }: { botId: string }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const access = useAccess()
  const raw = route.useSearch()
  const navigate = route.useNavigate()
  const deliveryId = useRef<number | undefined>(undefined)
  const [resolveOpen, setResolveOpen] = useState(false)
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: botRunsPersistence,
    raw: raw.runsQuery ?? botRunsSearchSchema.parse({}),
    accountId: access.session?.id ?? 0,
    navigate: ({ search: next }) =>
      navigate({
        search: {
          ...raw,
          runsQuery: botRunsSearchSchema.parse(
            typeof next === 'function' ? next(raw.runsQuery ?? botRunsSearchSchema.parse({})) : next
          )
        },
        replace: true
      })
  })
  const query = useQuery({
    ...trpc.customerCollection.runs.queryOptions({ ...search, botId }),
    placeholderData: keepPreviousData
  })
  const resolve = useMutation(
    trpc.customerCollection.resolveDelivery.mutationOptions({
      onSuccess: () => cache.invalidateQueries(trpc.customerCollection.runs.queryFilter({ botId }))
    })
  )
  const table = usePageTableNavigation<BotRunsSearch, BotRunsSearch>({
    defaultSearch: botRunsSearchSchema.parse({}),
    showTotal: (total) => `共 ${total} 条记录`,
    search,
    navigate: navigateSearch,
    getFormInitialValues: (value) => ({ ...value, keyword: value.keyword ?? value.id?.toString() }),
    getSearchFromForm: (value) => ({ ...value, id: undefined })
  })
  type Row = NonNullable<typeof query.data>['data'][number]
  const columns: ProColumns<Row>[] = [
    { title: 'ID', dataIndex: 'id', valueType: 'digit', copyable: true, search: false },
    {
      title: '关键词',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { placeholder: '记录 ID 或客户名称', allowClear: true }
    },
    { title: '客户', dataIndex: ['Customer', 'name'], search: false, ellipsis: true },
    { title: '账期', dataIndex: 'billingMonth', search: false },
    {
      title: '状态',
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: {
        preview: { text: '已预览', status: 'Default' },
        queued: { text: '待出单', status: 'Processing' },
        issued: { text: '已出单', status: 'Warning' },
        sent: { text: '已发送', status: 'Success' },
        failed: { text: '出单失败', status: 'Error' }
      }
    },
    { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
    { title: '处理信息', dataIndex: 'error', search: false, ellipsis: true },
    {
      title: '发送记录',
      key: 'deliveries',
      search: false,
      render: (_, row) => (
        <div className="space-y-1">
          {row.Deliveries.map((item) => (
            <div key={item.id}>
              #{item.groupId} ·{' '}
              {(
                {
                  pending: '待生成文件',
                  ready: '待发送',
                  sending: '发送中',
                  sent: '已发送',
                  failed: '发送失败',
                  unknown: '结果未知'
                } as Record<string, string>
              )[item.status] ?? item.status}
              {access.can('bill.group.send') && ['failed', 'unknown'].includes(item.status) && (
                <Button
                  type="link"
                  onClick={() => {
                    deliveryId.current = item.id
                    setResolveOpen(true)
                  }}
                >
                  核对处理
                </Button>
              )}
            </div>
          ))}
        </div>
      )
    }
  ]
  return (
    <>
      {query.error && <Alert type="error" title={query.error.message} />}
      <ProTable<Row, BotRunsSearch>
        headerTitle="自助出单记录"
        tooltip="记录客户通过机器人自助出单的预览、执行和投递结果。手动发送的收款账单请查看主动发送。"
        columnsState={{ persistenceKey: 'bot-runs-columns', persistenceType: 'localStorage' }}
        search={{ labelWidth: 'auto' }}
        locale={{ emptyText: '暂无出单记录，机器人执行出单后将在此展示' }}
        rowKey="id"
        columns={columns}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        pagination={{ ...table.pagination, total: query.data?.total ?? 0 }}
        options={{ reload: () => query.refetch() }}
        scroll={{ x: '55rem' }}
      />
      <ModalForm<{ action: 'retry' | 'confirm_sent'; reason: string }>
        title="核对发送结果"
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        modalProps={{
          destroyOnHidden: true,
          afterClose: () => {
            deliveryId.current = undefined
            resolve.reset()
          }
        }}
        onFinish={async (values) => {
          await resolve.mutateAsync({ ...values, id: deliveryId.current! })
          return true
        }}
      >
        <Alert type="warning" title="先核对企微群记录。结果未知时重新发送可能造成重复通知。" />
        {resolve.error && <Alert type="error" title={resolve.error.message} />}
        <Form.Item name="action" label="处理方式" rules={[{ required: true }]}>
          <Select
            options={[
              { label: '确认已发送', value: 'confirm_sent' },
              { label: '确认未发送，重新投递', value: 'retry' }
            ]}
          />
        </Form.Item>
        <Form.Item name="reason" label="核对依据" rules={[{ required: true, min: 5, max: 500 }]}>
          <Input.TextArea />
        </Form.Item>
      </ModalForm>
    </>
  )
}
